import { FileAudio, Fingerprint, Sparkles, Trash2, Workflow, X } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import { ApiError } from "@/lib/api";

/**
 * CAP-4 — the ghost trash button, top right, and the dialog behind it (design: the
 * recording header's delete, and the source detail's).
 *
 * Deletion is rare and provenance is sacred, so the button does nothing on its own: it
 * opens a dialog that names, in full, what a delete removes — the file, the pipeline
 * artifacts and the transcript, the insights drawn only from this source, and the
 * voiceprint samples it contributed — and says what survives, the insights another source
 * still evidences. Only "Delete permanently" calls the Server. The whole exchange exists so
 * that a file which is part of the world view's provenance is never removed by a stray click.
 */
export function DeleteSourceControl({ sourceId }: { sourceId: string }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Delete file"
        title="Delete file"
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-md)] text-[var(--text-muted)] transition-colors hover:bg-[var(--surface-glass)] hover:text-[var(--destructive)]"
      >
        <Trash2 size={16} strokeWidth={2} />
      </button>

      {open && <DeleteSourceDialog sourceId={sourceId} onClose={() => setOpen(false)} />}
    </>
  );
}

/** What a delete removes, spelled out — the design's cascade list, with the two tones it
 * uses: destructive for what is gone unconditionally, warning for what depends on the
 * evidence rule. */
const CASCADE = [
  {
    icon: FileAudio,
    tone: "var(--destructive)",
    text: "The source file and its audio — immediately and permanently.",
  },
  {
    icon: Workflow,
    tone: "var(--destructive)",
    text: "Every pipeline artifact, and the transcript, its segments and word timings.",
  },
  {
    icon: Sparkles,
    tone: "var(--warning)",
    text: "The insights drawn from it, with their entity mentions and search index rows.",
  },
  {
    icon: Fingerprint,
    tone: "var(--warning)",
    text: "Voiceprint samples this recording contributed to the gallery.",
  },
] as const;

function DeleteSourceDialog({ sourceId, onClose }: { sourceId: string; onClose: () => void }) {
  const { api, refreshRecordings, refreshRecentSources, refreshReview, refreshSetup } = useApp();
  const navigate = useNavigate();

  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function remove() {
    if (busy) return;

    setBusy(true);
    setProblem(null);
    try {
      await api.deleteSource(sourceId);
      // The list, the capture panel's "recently added" and the review badge can all have
      // just changed — a recording gone, and any of its pending insights with it.
      refreshRecordings();
      refreshRecentSources();
      refreshReview();
      refreshSetup();
      navigate("/recordings");
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "The source could not be deleted.");
      setBusy(false);
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-[color-mix(in_oklch,var(--background)_60%,transparent)] p-4 backdrop-blur-[var(--blur-sm)]"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Delete this source?"
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-[520px] rounded-[var(--radius-xl)] border border-[var(--border-default)] bg-[var(--surface-1)] p-[var(--space-6)] shadow-[var(--shadow-lg)]"
      >
        <div className="flex items-start justify-between gap-[var(--space-4)]">
          <div>
            <h2 className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">
              Delete this source?
            </h2>
            <p className="mt-1 text-[length:var(--text-sm)] text-[var(--text-secondary)]">
              Deleting is rare. Provenance is what makes the world view trustworthy.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-7 w-7 items-center justify-center rounded-[var(--radius-md)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
          >
            <X size={15} strokeWidth={2} />
          </button>
        </div>

        <div className="mt-[var(--space-6)] flex flex-col gap-[var(--space-4)]">
          <p className="text-[length:var(--text-sm)] leading-relaxed text-[var(--text-secondary)]">
            Only delete a file that was never meant to be ingested. This removes, permanently:
          </p>

          <div className="flex flex-col gap-[var(--space-2)] text-[length:var(--text-sm)] text-[var(--text-primary)]">
            {CASCADE.map(({ icon: Icon, tone, text }) => (
              <div key={text} className="flex items-start gap-[var(--space-2)]">
                <span className="shrink-0 pt-0.5" style={{ color: tone }}>
                  <Icon size={12} strokeWidth={2} />
                </span>
                <span className="flex-1">{text}</span>
              </div>
            ))}
          </div>

          <p className="rounded-[var(--radius-md)] border border-[var(--warning)] bg-[color-mix(in_oklch,var(--warning)_12%,transparent)] px-[var(--space-3)] py-[var(--space-2)] text-[length:var(--text-xs)] leading-relaxed text-[var(--text-secondary)]">
            An insight also evidenced by another source is kept, with the dead span from this one removed.
          </p>

          {problem && (
            <p role="alert" className="text-[length:var(--text-xs)] text-[var(--warning)]">
              {problem}
            </p>
          )}

          <div className="flex justify-end gap-[var(--space-2)]">
            <button
              type="button"
              onClick={onClose}
              className="h-8 rounded-[var(--radius-md)] border border-[var(--border-default)] px-[var(--space-3)] text-[length:var(--text-sm)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            >
              Keep the file
            </button>
            <button
              type="button"
              onClick={() => void remove()}
              disabled={busy}
              className="flex h-8 items-center gap-[var(--space-2)] rounded-[var(--radius-md)] bg-[var(--destructive)] px-[var(--space-3)] text-[length:var(--text-sm)] font-semibold text-[var(--text-inverse)] transition hover:brightness-110 disabled:opacity-50"
            >
              <Trash2 size={13} strokeWidth={2} />
              {busy ? "Deleting…" : "Delete permanently"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
